import React, { useMemo, useRef } from 'react';
import ChartsIndex from '@/pages/intelligence/components/ChartsComponents/ChartsIndex';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import { showTimeByDateType, newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import useSinglePieCharts from '@/pages/intelligence/components/Chart/hooks/useSinglePieCharts';
import useBarCharts from '@/pages/intelligence/components/Chart/hooks/useBarCharts';
import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import moment from 'moment';
import { getIfPercentage } from '@/pages/intelligence/utils/common';
import type { IChartsIndexInstance } from '@intelligence/components/ChartsComponents/ChartsIndex';

// 每个榜单，组装好各类可视化需要的数据，传递给可视化统一组件
const Visualis: React.FC<any> = (props: any) => {
  const {
    chartType = ChartType.Line,
    dateType,
    startDate,
    endDate,
    lineData = [], // 折线 数据
    isNodata,
    imageType,
    metric,
    titleNow,
    chartLoading,
    setChartLoading,
    topChartsType,
    renderType,
    tableData,
    gameName,
    segmentList,
  } = props;

  const cardRef = useRef(null);
  const chartsInstance = useRef<IChartsIndexInstance>(null);
  const isPercentLine = useMemo(() => {
    switch (renderType) {
      case RenderType.GROWTH_ONLY:
        return true;
      default:
        return false;
    }
  }, [topChartsType, renderType]);

  const barNameList = useMemo(() => {
    return (
      tableData?.map((item: any) => {
        const nameStr = item?.market?.toLocaleLowerCase() || item?.name || '';
        return nameStr;
      }) || []
    );
  }, [tableData]);

  const barSeriesData = useMemo(() => {
    return tableData?.map((item: any, i: number): any => {
      if (item.hasOwnProperty(metric)) {
        let barNum = item[metric]?.this ? item[metric]?.this : 0;
        if (getIfPercentage(metric)) {
          barNum = barNum * 100;
        }
        const nameStr = item?.market?.toLocaleLowerCase() || item?.name || '';
        return {
          type: 'bar',
          name: nameStr,
          value: barNum,
          market: item.market,
          itemStyle: {
            color: ECHARTSCOLOR[i],
          },
          label: {
            show: true,
            position: 'top',
            color: '#444',
            distance: 4,
            fontSize: 12,
            formatter: (params: any) =>
              `${newMathFormat(Number(params.value), true) + (getIfPercentage(metric) ? '%' : '')}`,
          },
        };
      }
    });
  }, [tableData, metric]);

  // 获取柱状图hooks
  const { barOption } = useBarCharts({
    seriesData: barSeriesData,
    barOrPieData: barSeriesData,
    setChartLoading,
    secondTitleRight: titleNow,
    barNameList,
    topChartsType: 'pcconsole-country-bar',
    cardWidth: cardRef?.current?.clientWidth,
    chartType,
    imageType,
    startDate,
    endDate,
  });

  const pieSeriesData = useMemo(() => {
    const piList: any = [];
    tableData?.forEach((item: any, i: any) => {
      if (item.hasOwnProperty(metric)) {
        const metricNum = item[metric]?.this ? item[metric]?.this : 0;
        const nameStr = item?.name || item?.market || '';
        const objTemp = {
          app_id: item.market,
          name: nameStr,
          value: metricNum,
        };
        piList.push(objTemp);
      }
    });
    return piList;
  }, [tableData, metric]);

  // 获取饼图hooks
  const { pieOption } = useSinglePieCharts({
    pieData: pieSeriesData,
    pieTitle: gameName,
    setChartLoading,
    chartLoading,
    chartType,
    chartsInstance,
    secondTitleRight: titleNow,
    startDate,
    endDate,
  });

  // 组装rpd折线图数据
  const getRpdTypeData = (list: any) => {
    if (!Array.isArray(list) || list?.length < 1) return [];
    const arr: any = [];
    for (const i in list) {
      arr.push({
        time: showTimeByDateType(list[i].date, dateType?.toLocaleLowerCase()),
        value: list[i].value || null,
      });
    }
    return arr;
  };

  const getMetricsList = (list: any, segment: string) => {
    if (!Array.isArray(list) || list?.length < 1) return [];
    const arr: any = [];
    for (const n in list) {
      const hisList =
        segment == 'digitalRevenue'
          ? list[n].revenue_data
          : segment == 'units'
          ? list[n].units_data
          : [];
      const objTemp = {
        id: list[n].market,
        name: list[n].name,
        history: getRpdTypeData(hisList),
      };
      arr.push(objTemp);
    }
    return arr;
  };

  const lineSeriesData = useMemo(() => {
    const arrGameData: any = {};
    for (const n in segmentList) {
      arrGameData[segmentList[n].value] = getMetricsList(lineData || [], segmentList[n].value);
    }
    return arrGameData;
  }, [lineData, metric, segmentList]);

  // 获取折线图hooks
  const { lineOption } = useLineCharts({
    lineData: lineSeriesData,
    metric,
    // topChartsType: 'pcconsole-stats-line',
    imageType,
    secondTitleRight: titleNow,
    chartType,
    setChartLoading,
    dateType,
    isPercentLine,
  });

  return (
    <>
      <div className="card-line" ref={cardRef}>
        <ChartsIndex
          ref={chartsInstance}
          chartLoading={chartLoading}
          lineOption={lineOption}
          barOption={barOption}
          pieOption={pieOption}
          chartType={chartType}
          isNodata={isNodata}
        />
      </div>
    </>
  );
};

export default Visualis;
